import { ColumnTypes } from '../models/SheetModel';

export class CellValidationHelper {
    /**
     * Creates a validation rule for a column type
     */
    static createColumnTypeValidation(columnType) {
        return {
            type: 'list',
            allowBlank: true,
            formulae: [`"${columnType.label}"`]
        };
    }

    /**
     * Creates a validation rule for column options
     */
    static createOptionsValidation(options) {
        return {
            type: 'list',
            allowBlank: true,
            formulae: [`"${options.join(',')}"`]
        };
    }

    /**
     * Handles validation setup for a new column
     */
    static setupNewColumnValidation(worksheet, columnIndex, columnType, options = []) {
        // --- MODIFICATION START: Do NOT set header validation for CHECKBOX --- 
        if (columnType.id !== ColumnTypes.CHECKBOX.id) {
            // Set header cell validation (column type label) for non-checkbox types
            const headerCell = worksheet.getCell(1, columnIndex);
            headerCell.dataValidation = this.createColumnTypeValidation(columnType);
        } else {
            // Optionally clear header validation if it was somehow set before
            const headerCell = worksheet.getCell(1, columnIndex);
            if (headerCell.dataValidation) {
                headerCell.dataValidation = undefined;
            }
        }
        // --- MODIFICATION END ---

        // Set data validation for the column if it has custom validation
        if (columnType.hasCustomValidation) {
            const validation = this.createOptionsValidation(
                options.length > 0 ? options : columnType.defaultOptions || []
            );
            this.applyColumnValidation(worksheet, columnIndex, validation);
        }
    }

    /**
     * Handles validation preservation when deleting columns
     */
    static handleColumnDeletion(worksheet, columnIndex) {
        const validations = new Map();
        
        // Store validations for all columns after the deleted one
        for (let row = 1; row <= worksheet.rowCount; row++) {
            for (let col = columnIndex + 1; col <= worksheet.columnCount; col++) {
                const cell = worksheet.getCell(row, col);
                if (cell.dataValidation) {
                    validations.set(`${row}-${col}`, { ...cell.dataValidation });
                }
            }
        }

        // Shift validations left
        validations.forEach((validation, key) => {
            const [row, col] = key.split('-').map(Number);
            const cell = worksheet.getCell(row, col - 1);
            cell.dataValidation = validation;
        });
    }

    /**
     * Handles validation preservation when moving columns
     */
    static handleColumnMove(worksheet, fromIndex, toIndex) {
        // Store all validations in the affected range
        const validations = new Map();
        const start = Math.min(fromIndex, toIndex);
        const end = Math.max(fromIndex, toIndex);
        
        // Collect all validations in the range
        for (let row = 1; row <= worksheet.rowCount; row++) {
            for (let col = start; col <= end; col++) {
                const cell = worksheet.getCell(row, col);
                if (cell.dataValidation) {
                    validations.set(`${row}-${col}`, { ...cell.dataValidation });
                }
            }
        }

        // Calculate new positions and apply validations
        validations.forEach((validation, key) => {
            const [row, col] = key.split('-').map(Number);
            let newCol;
            
            if (col === fromIndex) {
                newCol = toIndex;
            } else if (fromIndex < toIndex) {
                newCol = col > fromIndex && col <= toIndex ? col - 1 : col;
            } else {
                newCol = col >= toIndex && col < fromIndex ? col + 1 : col;
            }
            
            const cell = worksheet.getCell(row, newCol);
            cell.dataValidation = validation;
        });
    }

    /**
     * Handles validation preservation when moving rows
     */
    static handleRowMove(worksheet, fromIndex, toIndex) {
        const validations = new Map();
        
        // Store validations for the moving row
        for (let col = 1; col <= worksheet.columnCount; col++) {
            const cell = worksheet.getCell(fromIndex, col);
            if (cell.dataValidation) {
                validations.set(col, { ...cell.dataValidation });
            }
        }

        // Apply validations at new position
        validations.forEach((validation, col) => {
            const cell = worksheet.getCell(toIndex, col);
            cell.dataValidation = validation;
        });
    }

    /**
     * Handles validation preservation when moving multiple rows
     */
    static handleMultiRowMove(worksheet, rowIds, targetIndex) {
        const validations = new Map();
        
        // Store validations for all moving rows
        rowIds.forEach(rowIndex => {
            for (let col = 1; col <= worksheet.columnCount; col++) {
                const cell = worksheet.getCell(rowIndex, col);
                if (cell.dataValidation) {
                    validations.set(`${rowIndex}-${col}`, { ...cell.dataValidation });
                }
            }
        });

        // Apply validations at new positions
        let newRowIndex = targetIndex;
        rowIds.forEach(oldRowIndex => {
            for (let col = 1; col <= worksheet.columnCount; col++) {
                const validation = validations.get(`${oldRowIndex}-${col}`);
                if (validation) {
                    const cell = worksheet.getCell(newRowIndex, col);
                    cell.dataValidation = validation;
                }
            }
            newRowIndex++;
        });
    }

    /**
     * Applies validation to a range of cells in a column
     */
    static applyColumnValidation(worksheet, columnIndex, validation, skipHeader = true) {
        const startRow = skipHeader ? 2 : 1;
        for (let rowNumber = startRow; rowNumber <= worksheet.rowCount; rowNumber++) {
            const cell = worksheet.getCell(rowNumber, columnIndex);
            cell.dataValidation = { ...validation };
        }
    }

    /**
     * Updates column validation options
     */
    static updateColumnOptions(worksheet, columnIndex, options, columnType) {
        const validation = columnType.hasCustomValidation ? 
            this.createOptionsValidation(options) :
            this.createColumnTypeValidation(columnType);

        this.applyColumnValidation(worksheet, columnIndex, validation);
        return validation;
    }

    /**
     * Validates a value against column type and options
     */
    static validateValue(value, columnType, options = []) {
        if (value === null || value === undefined || value === '') {
            return true;
        }

        if (columnType.hasCustomValidation) {
            return options.includes(value.toString());
        }

        return columnType.validate(value);
    }

    /**
     * Preserves validations during batch operations
     */
    static preserveValidations(worksheet, operation) {
        const validations = new Map();
        
        // Store current validations
        for (let row = 1; row <= worksheet.rowCount; row++) {
            for (let col = 1; col <= worksheet.columnCount; col++) {
                const cell = worksheet.getCell(row, col);
                if (cell.dataValidation) {
                    validations.set(`${row}-${col}`, { ...cell.dataValidation });
                }
            }
        }

        // Perform the operation
        operation();

        // Restore validations with any necessary adjustments
        return validations;
    }

    /**
     * Handles all validation aspects of adding a new column
     */
    static handleNewColumn(worksheet, insertIndex, type, existingColumns) {
        // Store existing validations that need to shift
        const validations = new Map();
        
        // Store validations for columns that will shift
        for (let i = insertIndex; i < existingColumns.length; i++) {
            // Store header validation
            const headerCell = worksheet.getCell(1, i + 1);
            if (headerCell?.dataValidation) {
                validations.set(`1-${i}`, { ...headerCell.dataValidation });
            }

            // Store data validations
            for (let row = 2; row <= worksheet.rowCount; row++) {
                const cell = worksheet.getCell(row, i + 1);
                if (cell?.dataValidation) {
                    validations.set(`${row}-${i}`, { ...cell.dataValidation });
                }
            }
        }

        // Shift existing validations right
        validations.forEach((validation, key) => {
            const [row, col] = key.split('-').map(Number);
            const cell = worksheet.getCell(row, col + 1);
            cell.dataValidation = validation;
        });

        // Set up new column validation
        this.setupNewColumnValidation(worksheet, insertIndex + 1, type);
    }

    /**
     * Handles all validation aspects of deleting a column
     */
    static handleColumnDeletionComplete(worksheet, columnIndex, totalColumns) {
        console.log('Deleting column at index:', columnIndex);
        
        // Shift all columns left
        worksheet.eachRow((row, rowNumber) => {
            for (let i = columnIndex; i < totalColumns; i++) {
                const currentCell = row.getCell(i + 1);
                const targetCell = row.getCell(i);
                
                // Copy all cell properties
                targetCell.value = currentCell.value;
                targetCell.dataValidation = currentCell.dataValidation ? 
                    { ...currentCell.dataValidation } : null;
                Object.assign(targetCell.style, currentCell.style);
            }

            // Clear the last cell
            const lastCell = row.getCell(totalColumns);
            lastCell.value = null;
            lastCell.dataValidation = null;
            lastCell.style = {
                font: {},
                alignment: { vertical: 'bottom', horizontal: 'left' }
            };
        });

        console.log('Column deletion complete');
    }

    /**
     * Handles all validation aspects of moving a column
     */
    static handleColumnMoveComplete(worksheet, fromIndex, toIndex, totalColumns) {
        console.log('Moving column from', fromIndex, 'to', toIndex);

        // Store the entire column's data, including validation
        const columnData = [];
        worksheet.eachRow((row, rowNumber) => {
            const cell = row.getCell(fromIndex);
            columnData.push({
                value: cell.value,
                validation: cell.dataValidation ? { ...cell.dataValidation } : null,
                style: { ...cell.style }
            });
        });

        if (fromIndex < toIndex) {
            // Moving right: shift cells left in the gap
            worksheet.eachRow((row, rowNumber) => {
                for (let i = fromIndex; i < toIndex; i++) {
                    const currentCell = row.getCell(i + 1);
                    const targetCell = row.getCell(i);

                    targetCell.value = currentCell.value;
                    targetCell.dataValidation = currentCell.dataValidation ?
                        { ...currentCell.dataValidation } : null; // Copy validation
                    Object.assign(targetCell.style, currentCell.style);
                }
            });
        } else {
            // Moving left: shift cells right in the gap
            worksheet.eachRow((row, rowNumber) => {
                for (let i = fromIndex - 1; i >= toIndex; i--) {
                    const currentCell = row.getCell(i);
                    const targetCell = row.getCell(i + 1);

                    targetCell.value = currentCell.value;
                    targetCell.dataValidation = currentCell.dataValidation ?
                        { ...currentCell.dataValidation } : null; // Copy validation
                    Object.assign(targetCell.style, currentCell.style);
                }
            });
        }

        // Place stored column data in new position
        worksheet.eachRow((row, rowNumber) => {
            const targetCell = row.getCell(toIndex);
            // Ensure rowNumber-1 is a valid index for columnData
            if (rowNumber - 1 < columnData.length) {
                 const data = columnData[rowNumber - 1]; // Get the stored data for this row
                 targetCell.value = data.value;
                 targetCell.dataValidation = data.validation; // Apply stored validation
                 // Make sure style object exists before assigning
                 if (!targetCell.style) targetCell.style = {}; 
                 Object.assign(targetCell.style, data.style);
            } else {
                 // Handle cases where row might exist in worksheet but not in original columnData (less likely)
                 targetCell.value = null;
                 targetCell.dataValidation = null;
                 targetCell.style = {};
            }
        });

        console.log('Column move complete');
    }

    /**
     * Handles all validation aspects of moving rows
     */
    static handleRowMoveComplete(worksheet, fromIndices, toIndex, totalRows) {
        const validations = this.preserveValidations(worksheet, () => {
            if (Array.isArray(fromIndices)) {
                this.handleMultiRowMove(worksheet, fromIndices, toIndex);
            } else {
                this.handleRowMove(worksheet, fromIndices, toIndex);
            }
        });

        // Ensure proper validation state after move
        for (let row = 1; row <= totalRows; row++) {
            for (let col = 1; col <= worksheet.columnCount; col++) {
                const validation = validations.get(`${row}-${col}`);
                if (validation) {
                    const cell = worksheet.getCell(row, col);
                    cell.dataValidation = validation;
                }
            }
        }
    }

    /**
     * Handles all aspects of column insertion including validations
     */
    static handleColumnInsertion(worksheet, insertIndex, columns, newColumn) {
        const originalColumnCount = columns.length - 1; // Calculate original count BEFORE insertion
        console.log('DEBUG - Column insertion:', {
            insertIndex,
            originalColumnCount, // Log original count
            newColumnHeading: newColumn.heading,
            existingHeadings: worksheet.getRow(1).values?.slice(1, originalColumnCount + 1) // Log relevant original headings
        });

        worksheet.eachRow((row, rowNumber) => {
            const storedCells = [];
            // Store data from insert point to the *original* end
            const numCellsToStore = originalColumnCount - insertIndex;
            console.log(`DEBUG Row ${rowNumber} - Storing ${numCellsToStore} cells starting from Excel index ${insertIndex + 1}`); // Log count
            for (let i = 0; i < numCellsToStore; i++) {
                const cellIndex = insertIndex + 1 + i; // 1-based Excel index
                 // --- ADDED BOUNDS CHECK ---
                 if (cellIndex > originalColumnCount) {
                     console.warn(`DEBUG Row ${rowNumber} - Skipping storage for cell index ${cellIndex}, exceeds original count ${originalColumnCount}`);
                     continue; // Skip if index exceeds original bounds
                 }
                 // --- END ADDED BOUNDS CHECK ---
                const cell = row.getCell(cellIndex);
                storedCells.push({
                    value: cell.value,
                    validation: cell.dataValidation ? { ...cell.dataValidation } : null,
                    style: { ...cell.style }
                });
            }

            if (rowNumber === 1) {
                console.log('DEBUG - Stored header cells:', storedCells.map(c => c.value));
            }

            // Shift existing values right
            console.log(`DEBUG Row ${rowNumber} - Shifting ${storedCells.length} stored cells right, starting at target index ${insertIndex + 2}`); // Log shift info
            for (let i = storedCells.length - 1; i >= 0; i--) {
                const sourceData = storedCells[i];
                const targetCellIndex = insertIndex + 2 + i; // 1-based Excel index for the target

                // --- ADDED BOUNDS CHECK ---
                if (targetCellIndex > columns.length) {
                     console.warn(`DEBUG Row ${rowNumber} - Skipping shift for target index ${targetCellIndex}, exceeds NEW count ${columns.length}`);
                     continue; // Skip if target index exceeds new column count
                }
                // --- END ADDED BOUNDS CHECK ---

                const targetCell = row.getCell(targetCellIndex);

                targetCell.value = sourceData.value;

                // Apply validation (logic simplified for clarity, adjust if needed)
                if (rowNumber === 1) {
                     targetCell.dataValidation = sourceData.validation; // Copy header validation
                 } else if (sourceData.validation) {
                     targetCell.dataValidation = sourceData.validation; // Copy data validation if it exists
                 } else {
                     targetCell.dataValidation = null; // Clear validation otherwise
                 }

                Object.assign(targetCell.style, sourceData.style);
                 // --- ADDED: Clear the original cell after shifting ---
                 const originalCellIndex = insertIndex + 1 + i;
                 if (originalCellIndex <= originalColumnCount) { // Only clear within original bounds
                     const originalCell = row.getCell(originalCellIndex);
                     // Clear value and validation, keep style potentially? Adjust as needed
                     originalCell.value = null;
                     originalCell.dataValidation = null;
                     // originalCell.style = {}; // Optionally reset style
                 }
                 // --- END ADDED ---
            }
            // --- START NEW ADDITION (Corrected) ---
            if (rowNumber === 1) {
                 // After shifting, explicitly clear the cell immediately AFTER the new last column's index.
                 // This addresses potential lingering header cells affecting headerRow.cellCount.
                 const cellToClearIndex = columns.length + 1; // Index AFTER the new last column.
                 if (cellToClearIndex <= worksheet.columnCount) { // Check if this cell index actually exists in the worksheet row model
                     const cellToClear = row.getCell(cellToClearIndex);
                     // Only clear if it seems to have content (value is not null)
                     if (cellToClear && cellToClear.value !== null) {
                         console.log(`DEBUG Row ${rowNumber} - Explicitly clearing potential lingering header cell at index ${cellToClearIndex}`);
                         cellToClear.value = null;
                         cellToClear.dataValidation = null;
                         cellToClear.style = {}; // Reset style
                     }
                 }
            }
             // --- END NEW ADDITION (Corrected) ---

            // Set up the new cell at the insertion point
            const newCell = row.getCell(insertIndex + 1);

            if (rowNumber === 1) {
                // Set heading for the first row
                newCell.value = newColumn.heading;
                newCell.dataValidation = CellValidationHelper.createColumnTypeValidation(newColumn.type); // Use helper
                console.log('DEBUG - After setting new cell:', {
                    rowNumber,
                    cellValue: newCell.value,
                    currentHeadings: worksheet.getRow(1).values?.slice(1, columns.length + 1) // Log relevant current headings
                });
            } else {
                // Initialize data cells
                newCell.value = newColumn.type.defaultValue !== undefined ? newColumn.type.defaultValue : null;
                // Optionally apply default data validation for the new column type here if needed
                 if (newColumn.type.hasCustomValidation) {
                     newCell.dataValidation = CellValidationHelper.createOptionsValidation(newColumn.type.defaultOptions || []);
                 } else {
                     newCell.dataValidation = null; // Ensure no validation for types that don't need it
                 }
            }

            // Apply default style or specific style for new column
            newCell.style = {
                 font: { name: 'Arial', family: 2, size: 11, bold: rowNumber === 1 }, // Bold header
                 alignment: { vertical: 'center', horizontal: 'left' }, // Center vertically
                 // Add other default styles as needed
             };
        });

        // --- Optional: Clean up any cells beyond the new column count ---
         worksheet.eachRow((row, rowNumber) => {
             for (let colIdx = columns.length + 1; colIdx <= worksheet.actualColumnCount; colIdx++) {
                 const cell = row.getCell(colIdx);
                 cell.value = null;
                 cell.dataValidation = null;
                 cell.style = {}; // Reset style
             }
         });
         // Force recalculation of dimensions if needed (might not be necessary)
         // worksheet.dimensions = new Dimensions(); // Example, check ExcelJS docs
         // --- End Optional Cleanup ---


        console.log('DEBUG - Final headings:', worksheet.getRow(1).values?.slice(1, columns.length + 1)); // Log relevant final headings
    }
}